import React from "react";
import { Box, ButtonProps, Typography } from "@mui/material";
import classNames from "classnames";

import { ContainerButton, NavigationButtonIcon } from "./NavigationButton.styles";

export interface NavigationButtonProps extends Pick<ButtonProps, "variant" | "sx"> {
  // Icon to display on the button
  icon: React.ReactNode;

  // Size of the button's icon.
  iconSize?: "small" | "large";

  // Title text or element for the button
  title: string | React.ReactNode;

  // Optional subtitle text for the button
  subtitle?: string;

  // Indicates whether the button is checked (active state)
  checked?: boolean;

  // Target screen to navigate to when the button is clicked
  target?: string;

  // Callback function triggered on click if target is not set
  onClick?(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;

  // Disables the button if true
  disabled?: boolean;

  // Optional element to display on the right side of the button
  rightElement?: React.ReactNode;
}

const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon,
  title,
  subtitle,
  checked = false,
  target,
  onClick,
  disabled = false,
  rightElement,
  iconSize,
  ...props
}: NavigationButtonProps) => {
  const size = iconSize || (subtitle ? "large" : "small");
  return (
    <ContainerButton
      onClick={onClick}
      disabled={disabled}
      checked={checked}
      fullWidth
      variant="outlined"
      {...props}
      className={classNames({
        "navigation-button--checked": checked,
      })}
    >
      <Box display="flex" alignItems="center">
        <NavigationButtonIcon className="navigation-button-icon" size={size}>
          {icon}
        </NavigationButtonIcon>
        <Box>
          <Typography variant="body3" fontWeight="500" lineHeight="1.4" display="block">
            {title}
          </Typography>
          {subtitle && (
            <Typography fontSize={11} lineHeight="1.4" color={(theme) => theme.palette.biarri.secondary.grey}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {rightElement || <Box />}
    </ContainerButton>
  );
};

export default NavigationButton;
